import { ArrowLeft, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatPhonePT } from "@/lib/formValidation";

// Mostra mensagem de erro por baixo de um campo (com ícone e animação subtil).
export const FieldError = ({ message, id }) => {
  if (!message) return null;
  return (
    <motion.p
      id={id}
      role="alert"
      data-testid={id}
      initial={{ opacity: 0, y: -2 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className="text-[11px] font-medium text-red-600 dark:text-red-400 flex items-center gap-1.5 mt-1"
    >
      <AlertCircle size={11} className="shrink-0" />
      <span>{message}</span>
    </motion.p>
  );
};

// Classe utilitária para marcar um input como inválido
export const errorInputClass = (hasError) =>
  hasError
    ? "border-red-400 focus-visible:ring-red-400 dark:border-red-500/60 bg-red-50/40 dark:bg-red-500/5"
    : "";

export const Steps = ({ total, current }) => (
  <div className="flex gap-1.5 mb-1" data-testid="wizard-steps">
    {Array.from({ length: total }).map((_, i) => {
      const isActive = i === current;
      const isDone = i < current;
      return (
        <motion.span
          key={i}
          className="h-1 rounded-full overflow-hidden bg-slate-200 dark:bg-white/10"
          animate={{ width: isActive ? 32 : isDone ? 32 : 16 }}
          transition={{ type: "spring", stiffness: 220, damping: 28, mass: 0.6 }}
        >
          <motion.span
            className="block h-full bg-[#1698AC] rounded-full"
            initial={false}
            animate={{ width: isDone || isActive ? "100%" : "0%" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.span>
      );
    })}
  </div>
);

export const OptionCard = ({ selected, onClick, label, desc, image, icon: Icon, testId }) => {
  // Se `label` for JSX (retro-compat com wizards antigos), renderiza como antes.
  const labelIsString = typeof label === "string" || typeof label === "number";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left w-full border rounded-lg p-3 transition-all active:scale-[0.98] overflow-hidden ${
        selected
          ? "border-[#1698AC] bg-[#1698AC]/5 dark:bg-[#4FC6D8]/10 ring-1 ring-[#1698AC]"
          : "border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/25"
      }`}
    >
      {labelIsString ? (
        <div className="flex items-start gap-3">
          {image ? (
            <img
              src={image}
              alt={typeof label === "string" ? label : ""}
              loading="lazy"
              decoding="async"
              draggable="false"
              onError={(e) => {
                // Esconde a imagem se falhar; o fallback (icon ou nada) fica visível
                e.currentTarget.style.display = "none";
                if (e.currentTarget.nextSibling) {
                  e.currentTarget.nextSibling.style.display = "flex";
                }
              }}
              className="w-12 h-12 rounded-lg object-cover shrink-0 bg-slate-100 dark:bg-white/5"
            />
          ) : null}
          {/* Fallback (fica escondido se image tiver sucesso) */}
          {Icon && (
            <span
              className={`w-12 h-12 rounded-lg bg-[#1698AC]/10 text-[#1698AC] items-center justify-center shrink-0 ${image ? "hidden" : "flex"}`}
              aria-hidden="true"
            >
              <Icon size={20} />
            </span>
          )}
          <div className="flex-1 min-w-0 overflow-hidden">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 break-words">{label}</p>
            {desc && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2 break-words">{desc}</p>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{label}</div>
          {desc && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{desc}</p>
          )}
        </>
      )}
    </button>
  );
};

export const PrimaryBtn = ({ children, disabled, onClick, testId, type = "button" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className="flex-1 min-w-0 h-11 px-4 rounded-full bg-[#1698AC] text-white text-sm font-medium hover:bg-[#0F7484] active:scale-95 disabled:opacity-40 disabled:active:scale-100 transition-all inline-flex items-center justify-center gap-1 whitespace-nowrap overflow-hidden"
  >
    <span className="truncate">{children}</span>
  </button>
);

export const BackBtn = ({ onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-label="Voltar"
    className="w-11 h-11 rounded-full border border-slate-300 dark:border-white/20 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:border-[#1698AC] hover:text-[#1698AC] active:scale-95 transition-all shrink-0"
  >
    <ArrowLeft size={16} />
  </button>
);

export const IdentityFields = ({ prefix, values, onChange, errors = {}, touched = {}, onBlur }) => {
  // Mostra o erro apenas depois de o utilizador ter interagido com o campo (touched).
  const err = (field) => (touched[field] ? errors[field] : "") || "";
  const handleBlur = (field) => () => onBlur && onBlur(field);
  return (
  <div className="space-y-3">
    <div className="space-y-1.5">
      <Label htmlFor={`${prefix}-nome`}>Nome <span className="text-red-500">*</span></Label>
      <Input
        id={`${prefix}-nome`}
        data-testid={`${prefix}-nome-input`}
        required
        aria-invalid={!!err("nome")}
        aria-describedby={err("nome") ? `${prefix}-nome-error` : undefined}
        placeholder="O seu nome"
        value={values.nome || ""}
        onChange={(e) => onChange({ ...values, nome: e.target.value })}
        onBlur={handleBlur("nome")}
        className={errorInputClass(!!err("nome"))}
      />
      <FieldError id={`${prefix}-nome-error`} message={err("nome")} />
    </div>
    <div className="grid sm:grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label htmlFor={`${prefix}-email`}>Email <span className="text-red-500">*</span></Label>
        <Input
          id={`${prefix}-email`}
          data-testid={`${prefix}-email-input`}
          type="email"
          required
          aria-invalid={!!err("email")}
          aria-describedby={err("email") ? `${prefix}-email-error` : undefined}
          placeholder="o.seu@email.pt"
          value={values.email || ""}
          onChange={(e) => onChange({ ...values, email: e.target.value })}
          onBlur={handleBlur("email")}
          className={errorInputClass(!!err("email"))}
        />
        <FieldError id={`${prefix}-email-error`} message={err("email")} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${prefix}-telefone`}>Telefone <span className="text-red-500">*</span></Label>
        <Input
          id={`${prefix}-telefone`}
          data-testid={`${prefix}-telefone-input`}
          type="tel"
          required
          aria-invalid={!!err("telefone")}
          aria-describedby={err("telefone") ? `${prefix}-telefone-error` : undefined}
          placeholder="912 345 678"
          maxLength={20}
          value={values.telefone || ""}
          onChange={(e) => onChange({ ...values, telefone: formatPhonePT(e.target.value) })}
          onBlur={handleBlur("telefone")}
          className={errorInputClass(!!err("telefone"))}
        />
        <FieldError id={`${prefix}-telefone-error`} message={err("telefone")} />
      </div>
    </div>
    <p className="text-[11px] text-slate-400 leading-relaxed">
      Os seus dados são tratados de acordo com o RGPD, exclusivamente para resposta ao seu pedido.
    </p>
  </div>
  );
};

export const WizardShell = ({ open, onClose, title, desc, step, total, children, footer, testId }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent
      data-testid={testId}
      className="sm:max-w-lg max-h-[88vh] p-0 gap-0 flex flex-col overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-white/10"
    >
      <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-white/10 shrink-0">
        <DialogHeader>
          <Steps total={total} current={step} />
          <DialogTitle className="font-heading tracking-tight text-slate-900 dark:text-slate-100">{title}</DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-400">{desc}</DialogDescription>
        </DialogHeader>
      </div>
      <div data-lenis-prevent className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-visible px-6 py-4 bg-white dark:bg-slate-900">
        {children}
      </div>
      {footer && (
        <div className="px-6 py-3 border-t border-slate-100 dark:border-white/10 bg-white/95 dark:bg-slate-900/95 backdrop-blur shrink-0 flex gap-3 items-center">
          {footer}
        </div>
      )}
    </DialogContent>
  </Dialog>
);
